import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import SafeAvatar from '../SafeAvatar';
import {
  GET_COVERAGE_ELIGIBLE_USERS,
  GET_MY_COVERAGE
} from '../../graphql/my-availability-queries';
import { SET_MY_COVERAGE } from '../../graphql/my-availability-mutations';

const formatRole = (role) => {
  if (!role) return '';
  return role
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

function MyCoverageCard() {
  const [selectedId, setSelectedId] = useState('');

  const { data: coverageData, loading: coverageLoading, refetch: refetchCoverage } = useQuery(
    GET_MY_COVERAGE,
    { fetchPolicy: 'network-only' }
  );

  const { data: eligibleData, loading: eligibleLoading } = useQuery(
    GET_COVERAGE_ELIGIBLE_USERS,
    { fetchPolicy: 'cache-and-network' }
  );

  const currentCoverage = coverageData?.myCoverage || null;
  const eligibleUsers = useMemo(() => eligibleData?.coverageEligibleUsers || [], [eligibleData]);

  useEffect(() => {
    setSelectedId(currentCoverage?.id ? String(currentCoverage.id) : '');
  }, [currentCoverage]);

  const [saveCoverage, { loading: saving }] = useMutation(SET_MY_COVERAGE, {
    onCompleted: () => {
      toast.success('Coverage contact updated');
      refetchCoverage();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update coverage contact');
    }
  });

  const currentCoverageId = currentCoverage?.id ? String(currentCoverage.id) : '';
  const isDirty = selectedId !== currentCoverageId;
  const hasCoverage = Boolean(currentCoverage);

  const handleSelectChange = (event) => {
    setSelectedId(event.target.value);
  };

  const handleSave = () => {
    const coverageUserId = selectedId ? parseInt(selectedId, 10) : null;
    saveCoverage({ variables: { coverageUserId } });
  };

  const handleClear = () => {
    if (!hasCoverage) return;
    if (!window.confirm('Remove your coverage contact?')) return;
    setSelectedId('');
    saveCoverage({ variables: { coverageUserId: null } });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-1">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Coverage Contact</h2>
            <p className="text-sm text-gray-600 mt-1">
              Pick a teammate who handles your duties and communications when you're off.
              Reservations and managers can use this to know who to call when you're not available.
            </p>
          </div>
        </div>

        {coverageLoading ? (
          <div className="py-6 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center gap-3 p-3 rounded-md border border-gray-200 bg-gray-50">
              <SafeAvatar
                src={currentCoverage?.avatarUrl}
                alt={currentCoverage?.name || 'No coverage selected'}
                className="w-10 h-10 rounded-full"
                size={40}
              />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                  {currentCoverage ? currentCoverage.name : 'No coverage contact selected'}
                </span>
                <span className="text-xs text-gray-500">
                  {currentCoverage ? formatRole(currentCoverage.role) : 'Choose a teammate below to assign one'}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1">
                <label
                  htmlFor="my-coverage-select"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Assign Coverage
                </label>
                <select
                  id="my-coverage-select"
                  value={selectedId}
                  onChange={handleSelectChange}
                  disabled={eligibleLoading || saving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 disabled:opacity-50"
                >
                  <option value="">
                    {eligibleLoading ? 'Loading eligible users...' : 'No coverage contact'}
                  </option>
                  {eligibleUsers.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name} — {formatRole(person.role)}
                    </option>
                  ))}
                </select>
                {!eligibleLoading && eligibleUsers.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    No eligible teammates were found. Coverage contacts include active users outside housekeeping roles.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasCoverage && (
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={saving}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyCoverageCard;
